import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { ValueAdapter } from '../value.adapter';
import { OracleLinkedService } from 'app/model/linkedservice/oracle.linkedservice';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { DbConnectionStringBuilderService } from 'app/services/db-connection-string-builder.service';
import { Constants } from 'app/shared/shared-constants';
import { akvToModel, akvToResource, parameterizablePasswordOrAKVtoSecretBase, parameterizableSecretBaseToModel } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { isString, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { connectionStringPartToModel, connectionStringPartToResource } from 'app/shared/utils/linkedservice-parameter.util';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { AdapterContext, ConnectionAuthKind, ITridentResource, ParameterConfig } from 'shell-models-lib';
import { driverVersions } from 'app/registries/consts/tabular-consts';
import { oracleAdditionalPropertiesAdapts, oracleAdditionalPropertiesKeyList, OracleAdditionalPropertiesKeyType } from 'app/registries/consts/oracle-const';
import { toParameterClass, toParameterObject } from './parameter-config.util';
import { CONNECTOR_VERSION20 } from 'app/services/globals';

const oracleV2Adapts = {
    server: '*',
    authenticationType: '*',
    username: '*',
    ...oracleAdditionalPropertiesAdapts
};

export const oracleAdapts = {
    name: '*',
    properties: {
        description: '*',
        type: '*',
        connectVia: '*',
        version: '*',
        typeProperties: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            connectionString: (_context: AdapterContext, _value: any, ls: OracleLinkedService) => {
                if (ls && !ls.fullyByJsonEditor && !ls.enableAKV) {
                    // when AKV is used for connection string, it is already well handled. So we don't need handle it.
                    // won't cover fully by json for now
                    if (ls.password instanceof AzureKeyVault) {
                        return '*';
                    }
                }
                return 'secret';
            },
            ...oracleV2Adapts,
            password: 'secret',
            encryptedCredential: '*'
        },
        parameters: '*'
    }
};

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(OracleLinkedService),
    asynchronous: true,
    adapts: oracleAdapts
})
export class OracleLinkedServiceAdapter extends LinkedServiceAdapter<OracleLinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected modelConstructor(): new () => OracleLinkedService {
        return OracleLinkedService;
    }

    protected model(): IModel {
        return OracleLinkedService.MODEL;
    }

    protected override toAzureTypeProperties = (tridentResource: ITridentResource): Model.OracleLinkedServiceTypeProperties => {
        const credentialType = tridentResource.credentialDetails.credentialType;
        switch (credentialType) {
            case ConnectionAuthKind.Basic:
                const datasourceReference: Record<'kind' | 'path', string> = JSON.parse(
                    tridentResource.datasourceReference
                );
                return {
                    server: datasourceReference.path
                };
            default:
                return undefined;
        }
    };

    protected async propertiesToResourceAsync(linkedService: OracleLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.OracleLinkedService;
        properties.type = Metamodel.modelOf(linkedService).name;

        if (linkedService.version === driverVersions.v2) {
            properties.version = '2.0';
            properties.typeProperties = {
                server: this.valueToResource(linkedService, OracleLinkedService.SERVER),
                authenticationType: linkedService.authenticationType,
                username: this.valueToResource(linkedService, OracleLinkedService.USER_NAME),
                password: parameterizablePasswordOrAKVtoSecretBase(linkedService, OracleLinkedService.PASSWORD, this.context),
            }
            if (linkedService.parameterizableAdditionalFields?.length > 0) {
                properties.typeProperties = {
                    ...properties.typeProperties,
                    ...toParameterObject(linkedService.parameterizableAdditionalFields, false, this.valueToResource)
                };
            }
        } else {
            if (!linkedService.enableAKV) {
                const builder = new DbConnectionStringBuilderService(true);
                builder.add('host', connectionStringPartToResource(linkedService, OracleLinkedService.HOST_NAME));
                builder.add('port', connectionStringPartToResource(linkedService, OracleLinkedService.PORT) || '1521');
                if (linkedService.resourceType === 'sid') {
                    builder.add('sid', connectionStringPartToResource(linkedService, OracleLinkedService.SID));
                } else if (linkedService.resourceType === 'serviceName') {
                    builder.add('serviceName', connectionStringPartToResource(linkedService, OracleLinkedService.SERVICE_NAME));
                }
                builder.add('user id', connectionStringPartToResource(linkedService, OracleLinkedService.USER_NAME));

                if (!(linkedService.password instanceof AzureKeyVault)) {
                    const password = connectionStringPartToResource(linkedService, OracleLinkedService.PASSWORD);
                    if (password) {
                        builder.add('Password', password);
                    }
                }
                linkedService.additionalFields?.forEach((pair) => {
                    builder.add(pair.key, pair.value || '');
                });

                const connectionString = linkedService.hostName === Constants.maskedCredential &&
                    linkedService.port === undefined &&
                    linkedService.sid === Constants.maskedCredential &&
                    linkedService.serviceName === Constants.maskedCredential &&
                    linkedService.userName === Constants.maskedCredential &&
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    linkedService.password === Constants.maskedCredential ? {
                        type: 'SecureString',
                        value: linkedService.password === Constants.maskedCredential ? Constants.maskedCredential : builder.connectionString
                    } as Model.SecureString : this._connectionStringToResource(builder.connectionString);

                properties.typeProperties = {
                    connectionString,
                    password: linkedService.password instanceof AzureKeyVault ? akvToResource(linkedService.password, this.context) : undefined
                };
            } else {
                properties.typeProperties = {
                    connectionString: akvToResource(linkedService.referedAKV, this.context)
                };
            }
        }
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: OracleLinkedService): Promise<OracleLinkedService> {
        const properties = linkedServiceResource.properties as Model.OracleLinkedService;

        linkedService.version = __IS_FABRIC_DPE__
            ? driverVersions.v2
            : (properties.version === CONNECTOR_VERSION20 || properties.typeProperties.connectionString == null) ? driverVersions.v2 : driverVersions.v1;
        if (properties.typeProperties.connectionString == null) {
            linkedService.isLegacyConnector = false;
            ValueAdapter.toModel(linkedService, OracleLinkedService.SERVER, properties.typeProperties.server);
            ValueAdapter.toModel(linkedService, OracleLinkedService.USER_NAME, properties.typeProperties.username);
            linkedService.authenticationType = properties.typeProperties.authenticationType;
            parameterizableSecretBaseToModel(linkedService, OracleLinkedService.PASSWORD, properties.typeProperties.password, this.context);

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const additionalPropertiesObj: { [key: string]: any } = {};
            for (const propertyName in properties.typeProperties) {
                if (oracleAdditionalPropertiesKeyList.includes(propertyName)) {
                    additionalPropertiesObj[propertyName] =
                        properties.typeProperties[propertyName as keyof Model.OracleLinkedServiceTypeProperties];
                }
            }
            linkedService.parameterizableAdditionalFields = toParameterClass(
                additionalPropertiesObj,
                linkedService
            ) as Array<ParameterConfig<OracleAdditionalPropertiesKeyType>>;
        } else {
            linkedService.isLegacyConnector = true;
            OracleLinkedService.HOST_NAME.setValue(linkedService, undefined);
            OracleLinkedService.HOST_NAME.setTemplateValue(linkedService, undefined);
            OracleLinkedService.PORT.setValue(linkedService, undefined);
            OracleLinkedService.PORT.setTemplateValue(linkedService, undefined);
            OracleLinkedService.SID.setValue(linkedService, undefined);
            OracleLinkedService.SID.setTemplateValue(linkedService, undefined);
            OracleLinkedService.SERVICE_NAME.setValue(linkedService, undefined);
            OracleLinkedService.SERVICE_NAME.setTemplateValue(linkedService, undefined);
            OracleLinkedService.USER_NAME.setValue(linkedService, undefined);
            OracleLinkedService.USER_NAME.setTemplateValue(linkedService, undefined);
            OracleLinkedService.PASSWORD.setValue(linkedService, undefined);
            OracleLinkedService.PASSWORD.setTemplateValue(linkedService, undefined);
            linkedService.resourceType = 'sid';

            if (isString(properties.typeProperties.connectionString)) {
                const builder = new DbConnectionStringBuilderService(true);
                builder.parse(properties.typeProperties.connectionString as string);

                connectionStringPartToModel(linkedService, OracleLinkedService.HOST_NAME, builder.getValue('host', false) || '');
                connectionStringPartToModel(linkedService, OracleLinkedService.PORT, builder.getValue('port', false) || '1521');
                connectionStringPartToModel(linkedService, OracleLinkedService.SID, builder.getValue('sid', false) || '');
                const serviceName = builder.getValue('serviceName', false);
                if (!stringIsNullOrEmpty(serviceName)) {
                    linkedService.resourceType = 'serviceName';
                }
                connectionStringPartToModel(linkedService, OracleLinkedService.SERVICE_NAME, serviceName || '');
                connectionStringPartToModel(linkedService, OracleLinkedService.USER_NAME, builder.getValue('user id', false) || '');
                connectionStringPartToModel(linkedService, OracleLinkedService.PASSWORD, builder.getValue('password', false) || '', true);
                linkedService.additionalFields = builder.getRemainedKeyValuePairs(['host', 'port', 'sid', 'serviceName', 'user id', 'password']);
                if (properties.typeProperties.password) {
                    parameterizableSecretBaseToModel(linkedService, OracleLinkedService.PASSWORD, properties.typeProperties.password, this.context);
                }
            } else if (SecureStringReplacer.isSecureStringObject(properties.typeProperties.connectionString)) {
                linkedService.hostName = Constants.maskedCredential;
                linkedService.port = undefined;
                linkedService.sid = Constants.maskedCredential;
                linkedService.serviceName = Constants.maskedCredential;
                linkedService.userName = Constants.maskedCredential;
                linkedService.password = Constants.maskedCredential;
                if (properties.typeProperties.password) {
                    parameterizableSecretBaseToModel(linkedService, OracleLinkedService.PASSWORD, properties.typeProperties.password, this.context);
                }
            } else if (properties.typeProperties.connectionString && (properties.typeProperties.connectionString as Model.SecretBase).type === 'AzureKeyVaultSecret') {
                linkedService.referedAKV = akvToModel(properties.typeProperties.connectionString as Model.SecretBase, this.context);
                linkedService.enableAKV = true;
            }
        }

        return linkedService;
    }
}
